/* eslint-disable max-len */
import {SERVICE_NAMES} from '../constants';
import {createApiCall, fetchAllPages} from '../utils';
import type {ServiceName} from '../constants';
import type {
    ApiCallOptions,
    PaginatedResponse,
    Group,
    Member,
    CreateGroupRequest,
    UpdateGroupRequest,
    LinkMemberRequest,
    GroupsListParams,
    GroupMembersListParams,
    UniqueEmailParams,
    AccountUserMembership
} from './api.d';

/**
 * Group Service API client.
 *
 * Source: https://group-service.dev.validsign.eu/api/documentation/api.json
 * Synced: 2026-04-09
 */

const createGroupApiCall = <T = any>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH',
    endpoint: string
) => createApiCall<T>(SERVICE_NAMES.GROUP as ServiceName, method, endpoint);

export const groupApi = {
    // ========== Groups ==========

    /** GET /accounts/{accountId}/groups */
    getGroups: ({accountId, params, ...options}: {accountId: string; params?: GroupsListParams} & ApiCallOptions) =>
        createGroupApiCall<PaginatedResponse<Group>>('GET', `accounts/${accountId}/groups`)({...options, params}),

    /** GET /accounts/{accountId}/groups — all pages */
    getAllGroups: ({accountId, params, ...options}: {accountId: string; params?: Omit<GroupsListParams, 'page'>} & ApiCallOptions) => fetchAllPages(
        (paginationParams: GroupsListParams, opts?: ApiCallOptions) => groupApi.getGroups({
            accountId,
            params: {...(params || {}), ...paginationParams},
            ...opts
        }),
        {per_page: params?.per_page || 100} as {page?: number; per_page?: number},
        options
    ),

    /** GET /accounts/{accountId}/groups/{group} */
    getGroup: ({accountId, groupId, ...options}: {accountId: string; groupId: string} & ApiCallOptions) =>
        createGroupApiCall<Group>('GET', `accounts/${accountId}/groups/${groupId}`)({...options}),

    /** POST /accounts/{accountId}/groups */
    createGroup: ({accountId, data, ...options}: {accountId: string; data: CreateGroupRequest} & ApiCallOptions) =>
        createGroupApiCall<Group>('POST', `accounts/${accountId}/groups`)({...options, data}),

    /** PUT /accounts/{accountId}/groups/{group} */
    updateGroup: ({
        accountId, groupId, data, ...options
    }: {accountId: string; groupId: string; data: UpdateGroupRequest} & ApiCallOptions) =>
        createGroupApiCall<Group>('PUT', `accounts/${accountId}/groups/${groupId}`)({...options, data}),

    /** DELETE /accounts/{accountId}/groups/{group} */
    deleteGroup: ({accountId, groupId, ...options}: {accountId: string; groupId: string} & ApiCallOptions) =>
        createGroupApiCall<void>('DELETE', `accounts/${accountId}/groups/${groupId}`)({...options}),

    // ========== Group members ==========

    /** GET /accounts/{accountId}/groups/{group}/members */
    getGroupMembers: ({
        accountId, groupId, params, ...options
    }: {accountId: string; groupId: string; params?: GroupMembersListParams} & ApiCallOptions) =>
        createGroupApiCall<PaginatedResponse<Member>>('GET', `accounts/${accountId}/groups/${groupId}/members`)({...options, params}),

    /** POST /accounts/{accountId}/groups/{group}/members — link an account user as a group member */
    linkGroupMember: ({
        accountId, groupId, data, ...options
    }: {accountId: string; groupId: string; data: LinkMemberRequest} & ApiCallOptions) =>
        createGroupApiCall<Member>('POST', `accounts/${accountId}/groups/${groupId}/members`)({...options, data}),

    /** DELETE /accounts/{accountId}/groups/{group}/members/{member} — unlink a member from a group */
    unlinkGroupMember: ({
        accountId, groupId, memberId, ...options
    }: {accountId: string; groupId: string; memberId: string} & ApiCallOptions) =>
        createGroupApiCall<void>('DELETE', `accounts/${accountId}/groups/${groupId}/members/${memberId}`)({...options}),

    /** GET /members/{member} — fetch a single member by id */
    getMember: ({memberId, ...options}: {memberId: string} & ApiCallOptions) =>
        createGroupApiCall<Member>('GET', `members/${memberId}`)({...options}),

    // ========== Account user memberships ==========

    /** GET /account-users/{accountUserId}/memberships — groups the account user belongs to */
    getAccountUserMemberships: ({accountUserId, ...options}: {accountUserId: string} & ApiCallOptions) =>
        createGroupApiCall<AccountUserMembership[]>('GET', `account-users/${accountUserId}/memberships`)({...options}),

    // ========== Utility ==========

    /** GET /groups/unique-email — check whether an email is unique across groups */
    checkUniqueEmail: ({params, ...options}: {params: UniqueEmailParams} & ApiCallOptions) =>
        createGroupApiCall<Record<string, never>>('GET', 'groups/unique-email')({...options, params})
};
